import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function ComparatifCards() {
  const { t } = useTranslation('toiture');
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>(0.1);

  const cards = [
    {
      id: 'bardeau',
      image: 'https://readdy.ai/api/search-image?query=Close-up%20detail%20of%20new%20grey%20asphalt%20shingle%20roof%20on%20a%20Quebec%20house%2C%20overlapping%20architectural%20shingles%20in%20warm%20natural%20light%2C%20slate%20grey%20color%20with%20subtle%20texture%20variations%2C%20clean%20roofline%20against%20blue%20sky%2C%20professional%20real%20estate%20photography%2C%20no%20text%20visible&width=600&height=400&seq=toiture-bardeau1&orientation=landscape',
      alt: "Toiture bardeau d'asphalte neuve à Lévis",
      titleKey: 'card_bardeau_title',
      dureeKey: 'card_bardeau_duree',
      prixKey: 'card_bardeau_prix',
      plus1Key: 'card_bardeau_plus1',
      plus2Key: 'card_bardeau_plus2',
      minus1Key: 'card_bardeau_minus1',
      minus2Key: 'card_bardeau_minus2',
      ctaKey: 'card_bardeau_cta',
      link: '/bardeau-asphalte',
    },
    {
      id: 'tole',
      image: 'https://readdy.ai/api/search-image?query=Modern%20metal%20roof%20on%20a%20contemporary%20Quebec%20house%2C%20standing%20seam%20steel%20roofing%20in%20dark%20blue-grey%20color%2C%20sharp%20angular%20roof%20with%20snow%20on%20edges%2C%20warm%20afternoon%20light%2C%20architectural%20photography%20style%2C%20clean%20simple%20background%20with%20soft%20sky%2C%20no%20text%20visible&width=600&height=400&seq=toiture-tole1&orientation=landscape',
      alt: 'Toiture tôle métal neuve à Lévis',
      titleKey: 'card_tole_title',
      dureeKey: 'card_tole_duree',
      prixKey: 'card_tole_prix',
      plus1Key: 'card_tole_plus1',
      plus2Key: 'card_tole_plus2',
      minus1Key: 'card_tole_minus1',
      minus2Key: 'card_tole_minus2',
      ctaKey: 'card_tole_cta',
      link: '/toiture-tole-metal',
    },
    {
      id: 'membrane',
      image: 'https://readdy.ai/api/search-image?query=Flat%20roof%20with%20white%20elastomeric%20membrane%20on%20a%20modern%20Quebec%20building%2C%20smooth%20seamless%20surface%20with%20slight%20texture%2C%20professional%20commercial%20roofing%20detail%20shot%2C%20clean%20installation%20edges%20and%20flashing%2C%20neutral%20daylight%20with%20soft%20shadows%2C%20architectural%20photography%20style%2C%20no%20text%20visible&width=600&height=400&seq=toiture-membrane1&orientation=landscape',
      alt: 'Toit plat avec membrane élastomère à Lévis',
      titleKey: 'card_membrane_title',
      dureeKey: 'card_membrane_duree',
      prixKey: 'card_membrane_prix',
      plus1Key: 'card_membrane_plus1',
      plus2Key: 'card_membrane_plus2',
      minus1Key: 'card_membrane_minus1',
      minus2Key: 'card_membrane_minus2',
      ctaKey: 'card_membrane_cta',
      link: '/toit-plat-membrane-elastomere',
    },
    {
      id: 'blanc',
      image: 'https://readdy.ai/api/search-image?query=White%20cool%20roof%20on%20a%20residential%20house%20in%20Quebec%2C%20bright%20reflective%20white%20surface%20covering%20the%20entire%20roof%2C%20green%20trees%20surrounding%20the%20property%2C%20clean%20energy%20efficient%20roofing%20system%2C%20warm%20natural%20daylight%2C%20professional%20environmental%20architecture%20photography%2C%20no%20text%20visible&width=600&height=400&seq=toiture-blanc1&orientation=landscape',
      alt: 'Toiture blanche écologique à Lévis',
      titleKey: 'card_blanc_title',
      dureeKey: 'card_blanc_duree',
      prixKey: 'card_blanc_prix',
      plus1Key: 'card_blanc_plus1',
      plus2Key: 'card_blanc_plus2',
      minus1Key: 'card_blanc_minus1',
      minus2Key: 'card_blanc_minus2',
      ctaKey: 'card_blanc_cta',
      link: '/soumission-gratuite',
    },
  ];

  return (
    <section id="comparatif" className="bg-soft-gray py-20 md:py-28 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        {/* Title */}
        <div
          ref={titleRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2
            className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl mb-4"
            dangerouslySetInnerHTML={{ __html: t('comparatif_h2') }}
          />
        </div>

        {/* Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 transition-all duration-1000 delay-200 ${
            gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {cards.map((card, index) => (
            <div
              key={card.id}
              className="bg-white rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="h-48 md:h-56 overflow-hidden">
                <img
                  src={card.image}
                  alt={`${card.alt} — couvreur RBQ à Lévis`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>

              {/* Content */}
              <div className="p-5 md:p-6">
                <h3 className="font-manrope font-bold text-slate-primary text-lg md:text-xl mb-1 uppercase tracking-wide">
                  {t(card.titleKey)}
                </h3>
                <p className="text-copper font-semibold text-sm mb-1">
                  {t(card.dureeKey)}
                </p>
                <p className="text-gray-400 text-xs mb-4">
                  {t(card.prixKey)}
                </p>

                {/* Pros */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-0.5 shrink-0" />
                    <span className="text-gray-600 text-sm">{t(card.plus1Key)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-0.5 shrink-0" />
                    <span className="text-gray-600 text-sm">{t(card.plus2Key)}</span>
                  </div>
                </div>

                {/* Cons */}
                <div className="space-y-2 mb-5">
                  <div className="flex items-start gap-2">
                    <i className="ri-close-line text-red-400 mt-0.5 shrink-0" />
                    <span className="text-gray-500 text-sm">{t(card.minus1Key)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-close-line text-red-400 mt-0.5 shrink-0" />
                    <span className="text-gray-500 text-sm">{t(card.minus2Key)}</span>
                  </div>
                </div>

                {/* CTA */}
                <button
                  onClick={() => {
                    if (card.link === '/soumission-gratuite') {
                      const el = document.getElementById('quote-form');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      if (window.REACT_APP_NAVIGATE) window.REACT_APP_NAVIGATE(card.link);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-copper font-medium text-sm hover:gap-2 transition-all duration-300 whitespace-nowrap"
                >
                  {t(card.ctaKey)}
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}